import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import moment from 'moment';
import { Box, Typography } from '@mui/material';

import { FHAutocomplete, FHGenericSearch, FProgressBar } from '../components/base';
import {
  clientActions,
  claimsActions,
  providerActions,
  clientAssessmentActions,
  clientSupervisionActions,
  btSupervisionActions,
  intakeQuestionnaireActions,
  parentSupervisionActions,
  sessionActions,
  groupParentSupervisionActions,
  claimsInvoiceActions,
  onboardingQuestionnaireActions,
} from '../store';
import { FORTA_BLUE, FORTA_GRAY3 } from '../utils/colors';
import { Roles, Themes } from '../constants';
import { FeatureFlags } from '../constants/featureFlags';
import PageContainer from '../components/layout/PageContainer';
import HistoryTables from '../components/layout/billing-history/tables';
import {
  getQuestionnaireLoadPlan,
  populateClients,
  populateProviders,
} from '../components/layout/billing-history/utils';
import {
  claimSearchConfig,
  assessmentSearchConfig,
  btSNSearchConfig,
  clientSNSearchConfig,
  parentSNSearchConfig,
  sessionNoteSearchConfig,
  groupSNSearchConfig,
  invoiceSearchConfig,
  questionnaireSearchConfig,
  HistorySearchParams,
} from '../utils/common';
import { areUserRolesIn } from '../helpers';
import {
  HistoricalDocumentOptions,
  ClaimTargetIdPrefixMappings,
  HistoricalDocuments,
} from './constants';
import ErrorAlertIcon from '../components/svgs/ErrorAlertIcon';
import { ClinicalStatus, ProviderClinicalStatus } from '../admin/constants/general';

import type { IOption } from '../utils/types';
import type {
  IGenericSearchOutput,
  ISearchConfigList,
  SearchValueType,
} from '../components/base/FHGenericSearch';
import type { RootState } from '../store';
import type { ChangeEvent } from 'react';

function isClaimTargetIdKey(key: string): key is keyof typeof ClaimTargetIdPrefixMappings {
  return key in ClaimTargetIdPrefixMappings;
}

let abortController: AbortController | null = null;

function HistoricalNotes() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const [searchParams, setSearchParams] = useSearchParams();
  const filterNameFromUrl = searchParams.get('filterName');

  // Global state variables
  const { allClientsList: clients = [], loading: clientLoading } = useSelector(
    (state: RootState) => state.clients,
  );
  const {
    providersByRole: billers = [],
    allProvidersList = [],
    loading: billerLoading,
  } = useSelector((state: RootState) => state.providers);
  const { user } = useSelector((state: RootState) => state.auth);
  const isGuardian = areUserRolesIn(user?.roles, [Roles.Guardian]);
  const questionnaireEnabled = useBooleanFlagValue(FeatureFlags.QUESTIONNAIRE_ENABLED, false);
  const documentOptions = HistoricalDocumentOptions.filter(
    (option) =>
      (option.id !== HistoricalDocuments.Questionnaire || questionnaireEnabled) &&
      areUserRolesIn(user?.roles, option.rolesWithAccess),
  );

  const { loading: claimsLoading } = useSelector((state: RootState) => state.claims);
  const { loading: assessmentLoading } = useSelector((state: RootState) => state.clientAssessment);
  const { loading: clientSNLoading } = useSelector((state: RootState) => state.clientSupervisions);
  const { loading: btSNLoading } = useSelector((state: RootState) => state.btSupervision);
  const { loading: parentSNLoading } = useSelector((state: RootState) => state.parentSupervision);
  const { loading: groupParentSNLoading } = useSelector(
    (state: RootState) => state.groupParentSupervision,
  );
  const { loading: sessionsLoading } = useSelector((state: RootState) => state.sessions);
  const { loading: invoiceLoading } = useSelector((state: RootState) => state.claimsInvoice);
  const { onboardingQuestionnairesLoading } = useSelector(
    (state: RootState) => state.onboardingQuestionnaire,
  );
  const { intakeQuestionnairesLoading } = useSelector(
    (state: RootState) => state.intakeQuestionnaire,
  );

  // Local state variables
  const [prevSearchQuery, setPrevSearchQuery] = useState<{ [key: string]: string }>();
  const [searchConfig, setSearchConfig] = useState<ISearchConfigList>(claimSearchConfig);
  const [selectedDocument, setSelectedDocument] = useState<IOption | null>(null);
  const [searchOutput, setSearchOutput] = useState<IGenericSearchOutput | null>(null);

  useEffect(() => {
    if (!user) return;

    const firstAccessibleDocument = documentOptions[0] ?? null;

    if (filterNameFromUrl) {
      const selectedDoc = documentOptions.find((item) => item.id === filterNameFromUrl);
      if (selectedDoc) {
        setSelectedDocument(selectedDoc);
        sessionStorage.setItem('selectedHistoryDocument', JSON.stringify(selectedDoc));
        return;
      }
    }

    const selectedDocumentFromSessionStorage = sessionStorage.getItem('selectedHistoryDocument');
    if (selectedDocumentFromSessionStorage) {
      try {
        const parsedDocument = JSON.parse(selectedDocumentFromSessionStorage);
        const matchingOption = documentOptions.find(
          (documentOption) => documentOption.id === parsedDocument?.id,
        );
        if (matchingOption) {
          setSelectedDocument(matchingOption);
          return;
        }
      } catch {
        // ignore corrupt JSON and fall through
      }
      sessionStorage.removeItem('selectedHistoryDocument');
    }

    if (areUserRolesIn(user.roles, [Roles.Biller])) {
      setSelectedDocument(
        documentOptions.find(
          (documentOption) => documentOption.id === HistoricalDocuments.Claim,
        ) ?? firstAccessibleDocument,
      );
    } else if (areUserRolesIn(user.roles, [Roles.InvoiceCoordinator])) {
      setSelectedDocument(
        documentOptions.find(
          (documentOption) => documentOption.id === HistoricalDocuments.Invoice,
        ) ?? firstAccessibleDocument,
      );
    } else {
      setSelectedDocument(firstAccessibleDocument);
    }
  }, [user, filterNameFromUrl, questionnaireEnabled]);

  // Load required data for filters
  useEffect(() => {
    dispatch(
      clientActions.getList({
        getAccessTokenSilently,
        clinical_status: [
          ClinicalStatus.CLINICAL,
          ClinicalStatus.OFFBOARDED,
          ClinicalStatus.ON_HOLD,
          ClinicalStatus.PRE_CLINICAL,
        ],
      }),
    );
    return () => {
      dispatch(sessionActions.clearSession());
      dispatch(clientSupervisionActions.clearState());
      dispatch(claimsActions.clearState());
      dispatch(clientAssessmentActions.clearState());
      dispatch(btSupervisionActions.clearState());
      dispatch(parentSupervisionActions.clearState());
      dispatch(groupParentSupervisionActions.clearState());
      dispatch(claimsInvoiceActions.clearState());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user || isGuardian) {
      return;
    }
    dispatch(providerActions.getProvidersByRole({ getAccessTokenSilently, role: Roles.Biller }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isGuardian]);

  //Change search config based on selected document && populate clients and billers if required
  useEffect(() => {
    let updatedConfig: ISearchConfigList = [];
    switch (selectedDocument?.id) {
      case HistoricalDocuments.Claim:
        updatedConfig = populateClients(claimSearchConfig, clients);
        updatedConfig = populateProviders(updatedConfig, billers, HistorySearchParams.BillerId);
        setSearchConfig(updatedConfig);
        break;
      case HistoricalDocuments.Assessment:
        updatedConfig = populateClients(assessmentSearchConfig, clients);
        break;
      case HistoricalDocuments.BTSupervision:
        updatedConfig = populateClients(btSNSearchConfig, clients);
        break;
      case HistoricalDocuments.ClientSupervision:
        updatedConfig = populateClients(clientSNSearchConfig, clients);
        break;
      case HistoricalDocuments.GroupSupervision:
        updatedConfig = populateClients(groupSNSearchConfig, clients);
        break;
      case HistoricalDocuments.ParentSupervision:
        updatedConfig = populateClients(parentSNSearchConfig, clients);
        break;
      case HistoricalDocuments.Session:
        updatedConfig = populateClients(sessionNoteSearchConfig, clients);
        break;
      case HistoricalDocuments.Invoice:
        updatedConfig = populateClients(invoiceSearchConfig, clients);
        break;
      case HistoricalDocuments.Questionnaire:
        updatedConfig = populateClients(
          isGuardian
            ? questionnaireSearchConfig.filter(
                (item) =>
                  item.id !== HistorySearchParams.ProviderId &&
                  item.id !== HistorySearchParams.ProviderName,
              )
            : questionnaireSearchConfig,
          clients,
          true,
        );
        if (!isGuardian) {
          updatedConfig = populateProviders(
            updatedConfig,
            allProvidersList,
            HistorySearchParams.ProviderName,
          );
        }
        break;
      default:
        break;
    }
    setSearchConfig(updatedConfig);
  }, [billers, clients, allProvidersList, selectedDocument, isGuardian]);

  // Questionnaire rows only carry provider_id, so load the full provider list to resolve names
  useEffect(() => {
    if (
      selectedDocument?.id !== HistoricalDocuments.Questionnaire ||
      allProvidersList.length ||
      isGuardian
    ) {
      return;
    }
    dispatch(
      providerActions.getList({
        getAccessTokenSilently,
        include_roles: false,
        clinical_status: [
          ProviderClinicalStatus.CLINICAL,
          ProviderClinicalStatus.PRE_CLINICAL,
          ProviderClinicalStatus.OFFBOARDED,
          ProviderClinicalStatus.ON_HOLD,
          ProviderClinicalStatus.GUARDIAN_INVITED,
        ],
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDocument, isGuardian]);

  // Call expected endpoint based on search change
  useEffect(() => {
    if (!searchOutput || searchOutput?.length === 0) {
      setPrevSearchQuery({});
      dispatch(claimsActions.updateState({ key: 'list', data: [] }));
      dispatch(clientSupervisionActions.updateState({ key: 'list', data: [] }));
      dispatch(btSupervisionActions.updateState({ key: 'list', data: [] }));
      dispatch(parentSupervisionActions.updateState({ key: 'list', data: [] }));
      dispatch(sessionActions.updateState({ key: 'list', data: [] }));
      dispatch(clientAssessmentActions.updateState({ key: 'list', data: [] }));
      dispatch(groupParentSupervisionActions.updateState({ key: 'list', data: [] }));
      dispatch(claimsInvoiceActions.updateState({ key: 'list', data: [] }));
      return;
    }
    const output: Record<string, { value: SearchValueType }> = {};
    searchOutput.forEach((item) => {
      if (!item.id) return;
      output[item.id] = {
        value: item.value,
      };
    });
    const params: any = {};
    for (const key in output) {
      if (!output[key].value) {
        continue;
      }
      if (key === 'dateRange') {
        const selectedDateRange = output[key].value;
        if (!Array.isArray(selectedDateRange) || !selectedDateRange[0] || !selectedDateRange[1]) {
          continue;
        }
        const startDateVal = selectedDateRange[0];
        const endDateVal = selectedDateRange[1];

        if (startDateVal && endDateVal) {
          params['startDate'] = moment(startDateVal).startOf('day');
          params['endDate'] = moment(endDateVal).endOf('day');
        }
      } else {
        if (isClaimTargetIdKey(key)) {
          params[key] = String(output[key].value)
            .toLowerCase()
            .replace(ClaimTargetIdPrefixMappings[key].toLowerCase(), '');
          continue;
        }
        params[key] = output[key].value;
      }
    }
    setPrevSearchQuery(params);
    if (!Object.keys(params).length) {
      dispatch(claimsActions.updateState({ key: 'list', data: [] }));
      dispatch(clientSupervisionActions.updateState({ key: 'list', data: [] }));
      dispatch(btSupervisionActions.updateState({ key: 'list', data: [] }));
      dispatch(parentSupervisionActions.updateState({ key: 'list', data: [] }));
      dispatch(sessionActions.updateState({ key: 'list', data: [] }));
      dispatch(clientAssessmentActions.updateState({ key: 'list', data: [] }));
      dispatch(groupParentSupervisionActions.updateState({ key: 'list', data: [] }));
      dispatch(claimsInvoiceActions.updateState({ key: 'list', data: [] }));
      return;
    }

    if (JSON.stringify(params) === JSON.stringify(prevSearchQuery)) {
      return;
    }

    if (abortController) {
      abortController.abort();
    }
    abortController = new AbortController();
    const signal = abortController.signal;
    switch (selectedDocument?.id) {
      case HistoricalDocuments.Claim:
        dispatch(claimsActions.getList({ getAccessTokenSilently, params, signal }));
        break;
      case HistoricalDocuments.Assessment:
        dispatch(clientAssessmentActions.getList({ getAccessTokenSilently, params, signal }));
        break;
      case HistoricalDocuments.BTSupervision:
        dispatch(btSupervisionActions.getList({ getAccessTokenSilently, params, signal }));
        break;
      case HistoricalDocuments.ClientSupervision:
        dispatch(clientSupervisionActions.getList({ getAccessTokenSilently, params, signal }));
        break;
      case HistoricalDocuments.ParentSupervision:
        dispatch(parentSupervisionActions.getList({ getAccessTokenSilently, params, signal }));
        break;
      case HistoricalDocuments.GroupSupervision:
        dispatch(groupParentSupervisionActions.getList({ getAccessTokenSilently, params, signal }));
        break;
      case HistoricalDocuments.Questionnaire: {
        const { questionnaireParams, loadOnboarding, loadIntake } =
          getQuestionnaireLoadPlan(params);
        dispatch(
          loadOnboarding
            ? onboardingQuestionnaireActions.getOnboardingQuestionnaires({
                getAccessTokenSilently,
                params: questionnaireParams,
                signal,
              })
            : onboardingQuestionnaireActions.clearOnboardingQuestionnaires(),
        );
        dispatch(
          loadIntake
            ? intakeQuestionnaireActions.getIntakeQuestionnaires({
                getAccessTokenSilently,
                params: questionnaireParams,
                signal,
              })
            : intakeQuestionnaireActions.clearIntakeQuestionnaires(),
        );
        break;
      }
      case HistoricalDocuments.Session:
        dispatch(
          sessionActions.getList({
            getAccessTokenSilently,
            params: { ...params, include_qa_stats: true },
            signal,
          }),
        );
        break;
      case HistoricalDocuments.Invoice:
        dispatch(claimsInvoiceActions.getList({ getAccessTokenSilently, params, signal }));
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchOutput]);

  const isLoading =
    claimsLoading ||
    assessmentLoading ||
    clientSNLoading ||
    groupParentSNLoading ||
    btSNLoading ||
    parentSNLoading ||
    sessionsLoading ||
    clientLoading ||
    billerLoading ||
    invoiceLoading ||
    onboardingQuestionnairesLoading ||
    intakeQuestionnairesLoading;

  return (
    <PageContainer sx={{ px: 3 }} dataTestId="history-page-container">
      {isLoading && <FProgressBar />}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 3 }}>
        <Box>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Document
          </Typography>
          <FHAutocomplete
            data-testid="history-document-type"
            sx={{
              width: '260px',
              '& input::placeholder': {
                color: `${FORTA_GRAY3} !important`,
              },
            }}
            inputHeight={40}
            colortheme={Themes.Primary}
            coloredBackground
            disableClearable
            disabled={isLoading}
            value={selectedDocument}
            placeholder="Select Document"
            isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
            options={documentOptions}
            onChange={(_: ChangeEvent<HTMLSelectElement>, newValue: IOption | null) => {
              setSelectedDocument(newValue);
              setSearchParams({ filterName: newValue?.id?.toString() ?? '' });
              setSearchOutput(null);
              sessionStorage.setItem('selectedHistoryDocument', JSON.stringify(newValue));
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', ml: 2 }}>
          <FHGenericSearch
            chipStyles={{ mb: 1 }}
            name={selectedDocument?.id?.toString() ?? ''}
            autoPersistStrategy={'sessionStorage'}
            enableURLFilterParams
            chipWrapperId="historicalDocumentsSearchChips"
            disabled={isLoading}
            onFilterChange={(data: IGenericSearchOutput) => {
              setSearchOutput(data);
            }}
            searchConfig={searchConfig}
          />
        </Box>
      </Box>
      <Box
        id="historicalDocumentsSearchChips"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          height: 'auto',
          justifyContent: 'flex-start',
          mt: 1.5,
        }}
      ></Box>
      {!searchOutput?.length ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} my={2}>
          <ErrorAlertIcon width={18} height={18} viewBox="0 0 26 24" />
          <Typography
            data-testid={'coNoPayorClaimIdError'}
            textAlign={'left'}
            alignSelf="start"
            lineHeight={'18px'}
            ml={0.5}
            color={FORTA_BLUE.primary}
          >
            {`Click “Search” to generate a list of ${selectedDocument?.label}s`}
          </Typography>
        </Box>
      ) : (
        <HistoryTables selectedDocument={selectedDocument} />
      )}
    </PageContainer>
  );
}

export default HistoricalNotes;
